import React, {useContext, useState} from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {useAuth} from "../../../hooks/useAuth";
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import ConfirmSignUp from "../ConfirmSignUp/ConfirmSignUp";
import {ToasterContext} from "../../shared/Toaster/ToasterContext";
import {NotificationType} from "../../../constants/global";
import {routeConfigs} from "../../../navigation/routeConfig";
import {CircularProgress, InputAdornment} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {Visibility, VisibilityOff} from "@mui/icons-material";

const SignUp: React.FunctionComponent = () => {
  const {addToast} = useContext(ToasterContext);
  const auth: any = useAuth();
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

  const validationSchema = Yup.object().shape({
    given_name: Yup.string()
      .required('First name Required'),
    family_name: Yup.string()
      .required('Last name Required'),
    email: Yup.string()
      .required('Email is Required')
      .email('Email is invalid'),
    password: Yup.string()
      .required('Password is required')
      .min(8, 'Password is too short - should be 8 chars minimum.')
      .matches(/[a-zA-Z]/, 'Password can only contain Latin letters.'),
    confirmPassword: Yup.string()
      .required('Password is required')
      .oneOf([Yup.ref('password')], 'Your passwords do not match.'),
    phone_number: Yup.string()
      .required('Phone number Required')
      .matches(phoneRegExp, 'Phone number is not valid')
  });

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema)
  });
  const onSubmit = async (data: any) => {
    setLoading(true);
    const result = await auth.signUp({ ...data, username: data.email, phone_number: `+1${data.phone_number}`});
    setUsername(data.email);

    if (result.success) {
      setLoading(false);
      setIsConfirmed(true);
      setPassword(data.password);
      addToast({ type: NotificationType.Success, title: 'Thank you for signing up! We have sent a verification code to your email address. Please check your inbox and enter the code below to proceed with the verification process.' });
    } else {
      setLoading(false);
      setIsConfirmed(false);
      setPassword('');
      addToast({ type: NotificationType.Error,  title: result.message });
    }
  };

  return (
    <>
      {isConfirmed ? (
        <ConfirmSignUp setIsConfirmed={setIsConfirmed} username={username} password={password} />
      ) : (
        <>
          <Typography component="h1" variant="h5">
            Sign Up
          </Typography>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{mt: 2}}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  id="firstName"
                  fullWidth
                  required
                  helperText={errors.given_name?.message?.toString()}
                  {...register('given_name')}
                  error={!!errors.given_name}

                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  autoComplete="family-name"
                  label="Last Name"
                  id="lastName"
                  fullWidth
                  required
                  helperText={errors.family_name?.message?.toString()}
                  {...register('family_name')}
                  error={!!errors.family_name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  autoComplete="email"
                  label="Email Address"
                  id="email"
                  fullWidth
                  required
                  {...register('email')}
                  helperText={errors.email?.message?.toString()}
                  error={!!errors.email}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  autoComplete="current-password"
                  label="Password"
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  required
                  helperText={errors.password?.message?.toString()}
                  {...register('password')}
                  error={!!errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword((show) => !show)}
                          onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Confirm Password"
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  fullWidth
                  required
                  helperText={errors.confirmPassword?.message?.toString()}
                  {...register('confirmPassword')}
                  error={!!errors.confirmPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowConfirmPassword((show) => !show)}
                          onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  autoComplete="phone_number"
                  label="Phone number"
                  id="phone_number"
                  fullWidth
                  required
                  helperText={errors.phone_number?.message?.toString()}
                  {...register('phone_number')}
                  error={!!errors.phone_number}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+1</InputAdornment>,
                  }}
                />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{mt: 3, mb: 2}}
            >
              Sign Up
              { loading && <CircularProgress sx={{ml: 1}} size={14} color='inherit' />}
            </Button>
            <Grid container>
              <Grid item>
                Already have an account?
                <Link href={routeConfigs.login.path} sx={{ml: 1}} variant="body2">
                  Sign In
                </Link>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </>
  );
}

export default SignUp;